// Apolo IDE — GitRefMenu (Pedro 2026-06-25, sprint cursor-strip-3 T12).
//
// Drop-up no composer com commits recentes (REAIS, via IPC window.apolo.git
// .logSearch) pra Pedro citar/anexar uma referência de commit no draft.
// "adicione também o commit do chat" — esse é o item 5 do round 3.
//
// Click insere "#<sha7> <subject>\n" no draft via onInsert callback (parent =
// SessionComposer). Filter por subject/sha. Sem mockup: usa o git real do
// worktree da sessão.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GitCommit, Loader2, Search, X } from 'lucide-react';
import { hasElectronBridge } from '../../../lib/electronBridge.js';

const DEFAULT_LIMIT = 20;

export default function GitRefMenu({
  open,
  onClose,
  onInsert,
  root,
  anchorClassName = 'absolute bottom-full left-0 z-50 mb-2',
}) {
  const [loading, setLoading] = useState(false);
  const [commits, setCommits] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const menuRef = useRef(null);
  const filterRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!hasElectronBridge() || !window.apolo?.git?.logSearch) {
      setError('Listar commits só funciona no app Electron.');
      return;
    }
    setLoading(true);
    setError(null);
    window.apolo.git
      .logSearch({ root, limit: DEFAULT_LIMIT })
      .then((res) => {
        if (!res?.ok) {
          setError(res?.error || 'Falha ao ler git log.');
          setCommits([]);
        } else {
          setCommits(Array.isArray(res.commits) ? res.commits : []);
        }
      })
      .catch((e) => setError(e?.message || 'Falha ao ler git log.'))
      .finally(() => setLoading(false));
  }, [open, root]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => filterRef.current?.focus());
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose?.();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return commits;
    return commits.filter(
      (c) => c.subject?.toLowerCase().includes(q) || c.sha?.toLowerCase().startsWith(q),
    );
  }, [commits, filter]);

  const handlePick = useCallback(
    (c) => {
      if (!c?.sha) return;
      const sha = c.sha.slice(0, 7);
      const ref = `#${sha} ${c.subject || ''}`.trim();
      onInsert?.(ref);
      onClose?.();
    },
    [onInsert, onClose],
  );

  if (!open) return null;
  const electron = hasElectronBridge();

  return (
    <div
      ref={menuRef}
      role="dialog"
      aria-label="Inserir referência de commit"
      data-testid="git-ref-menu"
      className={
        anchorClassName +
        ' min-w-[320px] max-w-[460px] rounded-md border border-apolo-borderSubtle bg-apolo-surface1 p-1 shadow-xl backdrop-blur-md'
      }
    >
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-wide text-apolo-textTertiary">
          <GitCommit size={11} aria-hidden="true" />
          Inserir commit
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="inline-flex h-4 w-4 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
        >
          <X size={11} aria-hidden="true" />
        </button>
      </div>

      {electron && (
        <div className="mx-2 mb-1 flex items-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1">
          <Search size={11} aria-hidden="true" className="text-apolo-textTertiary" />
          <input
            ref={filterRef}
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Buscar por mensagem ou SHA…"
            aria-label="Filtrar commits"
            data-testid="git-ref-filter"
            className="block flex-1 bg-transparent text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
          />
        </div>
      )}

      {loading && (
        <p className="px-2 py-1.5 text-[11px] text-apolo-textTertiary">
          <Loader2 size={11} aria-hidden="true" className="mr-1 inline-block animate-spin" />
          Lendo git log…
        </p>
      )}

      {!loading && electron && filtered.length === 0 && (
        <p className="px-2 py-1.5 text-[11px] text-apolo-textTertiary">
          Nenhum commit encontrado.
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <ul className="flex max-h-[280px] flex-col overflow-y-auto">
          {filtered.map((c) => (
            <li key={c.sha}>
              <button
                type="button"
                onClick={() => handlePick(c)}
                data-testid="git-ref-commit"
                className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
              >
                <span className="mt-0.5 shrink-0 font-mono text-[10.5px] text-apolo-textTertiary">
                  #{c.sha.slice(0, 7)}
                </span>
                <span className="min-w-0 flex-1 truncate" title={c.subject}>
                  {c.subject || '(sem mensagem)'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!electron && (
        <p className="px-2 py-1.5 text-[10.5px] text-apolo-textTertiary">
          Listar commits só funciona no app Electron.
        </p>
      )}
      {error && (
        <p role="alert" className="px-2 py-1.5 text-[10.5px] text-apolo-error">{error}</p>
      )}
    </div>
  );
}
